'use client';

import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import { Button, Grid, TextLink } from '@petsmart-ui/sparky';
import { AUTH_COOKIES, COOKIES, OG_COOKIE } from '@libs/core';
import type { IdentityUser } from '@libs/common-next';
import {
    AccountDrawerType,
    clsx,
    decodeJwtCookie,
    getNextApiRoute,
    GoogleRecaptchaCheckbox,
    PageType,
    recaptchaId,
    typedFetch,
    useCart,
    useGoogleRecaptcha,
    usePageLayout,
    useUserSession,
    logSentryError,
    SentryAcpSignInErrors as SentryErrors,
} from '@libs/common-next';
import { useEffect, useMemo, useState } from 'react';

import { usePathname } from 'next/navigation';
import type { IdentityRouteAuthMergeResponse } from '@libs/experience-api';
import {
    NEW_TAB,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    TARGET_BLANK,
    TARGET_SELF,
} from '@libs/contentful';
import { trackError } from '@libs/analytics';
import Cookies from 'js-cookie';
import type { CartValidationResponse } from '@libs/common-node';
import { ValidationAlerts } from '../ValidationAlert';
import { EmailField } from './EmailField';
import { validateSignInForm } from './schema';
import type { SignInFormValues } from './types';
import type {
    InterstitialCta,
    InterstitialLinkAction,
} from '../InterstitialSignIn';
import { PasswordField } from './PasswordField';

import {
    ACTIVATE_OFFER_PAGE,
    CART_PAGE,
    CHECKOUT_PAGE,
    CREATE_ACCOUNT_PAGE,
    emailExistsMsg,
    SIGN_IN_PAGE,
    TREATS_REWARDS_AUTHENTICATED_PAGE,
    TREATS_REWARDS_PAGE,
    RESET_PASSWORD,
} from './constants';
import { showMergedCartBanner, windowRedirect } from '../../utils';

import './SignInForm.scss';

const initialFormState: SignInFormValues = {
    email: '',
    password: '',
    isExistingEmail: false,
};

export type SignInFormProps = {
    cta?: InterstitialCta;
    linkAction?: InterstitialLinkAction;
    isAutoship?: boolean;
    isInDrawer?: boolean;
    redirectTo?: string;
    onSuccessfulSignIn?: () => Promise<void> | void;
};

const componentClassname = 'sign-in-form';

const pagesToRedirect = [
    TREATS_REWARDS_PAGE,
    CREATE_ACCOUNT_PAGE,
    SIGN_IN_PAGE,
    ACTIVATE_OFFER_PAGE,
    RESET_PASSWORD,
];

// TODO: try to replace with useRef to prevent re-render.  Had trouble unit testing useRef  Or possibly try window !== undefined
function useIsRendered() {
    const [isRendered, setIsRendered] = useState(false);

    useEffect(() => {
        setIsRendered(true);
    }, []);

    return isRendered;
}

export function SignInForm({
    cta,
    linkAction,
    isInDrawer = false,
    onSuccessfulSignIn,
}: Readonly<SignInFormProps>) {
    const [isFetching, setIsFetching] = useState(false);
    const [authResponse, setAuthResponse] =
        useState<IdentityRouteAuthMergeResponse | null>(null);
    const [emailExists, setEmailExists] = useState(false);
    const isRendered = useIsRendered();

    const {
        accountDrawersOpen,
        isAccountDrawerOpen,
        flags,
        openAccountDrawer,
        removeAccountDrawers,
        pageType,
    } = usePageLayout();
    const { setUser } = useUserSession();
    const {
        handleRecaptcha,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        resetRecaptchaCheckbox,
        resetRecaptchaValues,
    } = useGoogleRecaptcha(!!flags.loginRecaptchaEnabled);
    const { setCart, refetchCart } = useCart();

    const pathname = usePathname();
    const isCheckout = pathname?.includes('checkout-login');

    const idPrefix = useMemo(
        () => (isInDrawer ? 'sign-in-drawer-' : ''),
        [isInDrawer]
    );

    function handleRedirect(
        isCartValidationSuccessful: boolean,
        updatedCartId?: string
    ) {
        const isCartPage = pageType === PageType.CART;
        const isCheckoutPage = pageType === PageType.CHECKOUT;

        if (isCartPage || isCheckoutPage) {
            if (isCartValidationSuccessful) {
                if (isCartPage && updatedCartId) {
                    // No action needed on the cart page because updated cart
                    // is already in state after successful login.
                    return;
                }

                // TODO: convert to router.push once upgrade to Next 15
                windowRedirect(CHECKOUT_PAGE);
                return;
            }

            // we are forcing refresh in order to display merged cart,
            // should be improved once better solution is found
            // TODO: convert to router.push once upgrade to Next 15
            windowRedirect(CART_PAGE);
            return;
        }

        if (
            pathname &&
            pagesToRedirect.some(page => pathname.startsWith(page))
        ) {
            // TODO: convert to router.push once upgrade to Next 15
            windowRedirect(TREATS_REWARDS_AUTHENTICATED_PAGE);
        }
    }

    function handleInvalidSubmit({
        email,
        password,
    }: {
        email?: string[];
        password?: string[];
    }) {
        const errorMessage = [...(email ?? []), ...(password ?? [])];
        if (emailExists) {
            errorMessage.push(emailExistsMsg);
        }
        trackError('login', errorMessage.join(' | '));
    }

    const checkboxId = isInDrawer
        ? recaptchaId.loginDrawerCheckbox
        : recaptchaId.loginCheckbox;
    const scoreId = isInDrawer
        ? recaptchaId.loginDrawerScore
        : recaptchaId.loginScore;
    const isDrawerOpen = accountDrawersOpen?.includes(
        AccountDrawerType.SIGN_IN
    );
    const isDrawerFormOpen = isInDrawer && isDrawerOpen;
    const isNonDrawerFormOpen = !isInDrawer && !isAccountDrawerOpen;

    // TODO: consider moving some of this logic up into useGoogleRecaptcha hook
    useEffect(() => {
        setAuthResponse(null);
        setUseRecaptchaCheckbox(null);
        resetRecaptchaValues();
    }, [isDrawerFormOpen, isNonDrawerFormOpen]);

    async function validateCartMerge(cartId?: string): Promise<boolean> {
        if (!cartId) {
            return false;
        }

        try {
            const result = await typedFetch<CartValidationResponse>(
                getNextApiRoute(`carts/${cartId}/validateCartMerge`),
                { method: 'PUT' }
            );

            return !!(result && 'size' in result && 'timeout' in result);
        } catch (error) {
            logSentryError(error, SentryErrors.SignInForm.ValidateCartMerge);
            return false;
        }
    }

    async function handleSubmitForm(values: SignInFormValues) {
        if (isFetching) return; // prevent double submit

        // prevent multiple sign in from multiple browser tabs
        const accessToken = Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
        if (accessToken) {
            refetchCart();
            handleSuccessfullSignIn(false);
            return;
        }

        setIsFetching(true);

        try {
            const resultRecaptcha = await handleRecaptcha(scoreId);

            if (!resultRecaptcha) {
                setIsFetching(false);
                return;
            }

            const res = await typedFetch<IdentityRouteAuthMergeResponse>(
                getNextApiRoute(`auth`),
                {
                    method: 'POST',
                    body: JSON.stringify({
                        email: values.email,
                        password: values.password,
                    }),
                    credentials: 'include',
                    additionalSuccessCodes: [401],
                }
            );

            const updatedCart = res?.data?.updatedCart ?? res?.data?.cart;

            if (updatedCart?.id && updatedCart.version) {
                showMergedCartBanner(Cookies.get(COOKIES.CART_ID), updatedCart);
                Cookies.set(COOKIES.CART_ID, updatedCart.id);
                setCart(updatedCart);
            }

            setAuthResponse(res);
            setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));

            if (res?.success) {
                const isCartMergeSuccessful =
                    pageType === PageType.CART || pageType === PageType.CHECKOUT
                        ? await validateCartMerge(updatedCart?.id)
                        : false;
                handleSuccessfullSignIn(isCartMergeSuccessful);
            } else {
                throw Error;
            }
        } catch (err) {
            logSentryError(err, SentryErrors.SignInForm.SubmitFailed);

            if (useRecaptchaCheckbox) {
                resetRecaptchaCheckbox('failed');
            } else {
                setUseRecaptchaCheckbox(checkboxId);
            }

            setIsFetching(false);
        }
    }

    async function handleSuccessfullSignIn(isCartMergeSuccessful: boolean) {
        setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));
        const cartId = Cookies.get(COOKIES.CART_ID);

        setUseRecaptchaCheckbox(null);

        await onSuccessfulSignIn?.();

        removeAccountDrawers([AccountDrawerType.SIGN_IN]);

        /**
         * Remove Order Groove cookie in order to avoid potential issues
         */
        Cookies.remove(OG_COOKIE);

        handleRedirect(isCartMergeSuccessful, cartId);
    }

    function RenderCTA({
        validateForm,
    }: {
        validateForm: (
            values?: unknown
        ) => Promise<FormikErrors<SignInFormValues>>;
    }) {
        const buttonType = isRendered ? 'submit' : 'button';
        const buttonStyle = cta?.fields.buttonStyle.toLowerCase();
        const ctaVariant =
            buttonStyle === 'primary'
                ? PRIMARY_BUTTON_STYLE
                : SECONDARY_BUTTON_STYLE;
        const buttonProps = {
            'data-testid': 'signInCta',
            fullWidth: true,
            hideText: isFetching,
            loading: isFetching,
            text: cta?.fields?.text ?? 'Sign in',
            type: buttonType as typeof buttonType,
        };

        return cta ? (
            <a
                target={
                    cta.fields.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF
                }
                href={cta?.fields?.link?.fields?.webUrl ?? '#'}
                rel="noreferrer"
            >
                <Button {...buttonProps} size="lg" variant={ctaVariant} />
            </a>
        ) : (
            <Button
                {...buttonProps}
                onClick={async () => {
                    if (isRendered) {
                        const result = await validateForm();

                        if (Object.keys(result).length > 0) {
                            handleInvalidSubmit(
                                result as {
                                    [key: string]: string[];
                                }
                            );
                        }
                    }
                }}
                size="lg"
                variant="primary"
            />
        );
    }

    return (
        <Formik
            onSubmit={handleSubmitForm}
            initialValues={initialFormState}
            validate={validateSignInForm}
            validateOnBlur
        >
            {({ validateForm }) => (
                <Form noValidate>
                    {authResponse?.data?.code === 401 && (
                        <ValidationAlerts
                            defaultHeading={authResponse.message?.header}
                            validationErrors={[
                                { message: authResponse.message?.body },
                            ]}
                        />
                    )}
                    <Grid gap="md">
                        <Grid.Item
                            className={clsx(
                                `${componentClassname}`,
                                isCheckout && `${componentClassname}--checkout`
                            )}
                        >
                            <EmailField
                                uniqueId={`${idPrefix}email`}
                                emailExists={emailExists}
                                setEmailExists={setEmailExists}
                            />
                            <PasswordField
                                data-testid="testingId"
                                uniqueId={`${idPrefix}password`}
                            />
                            {useRecaptchaCheckbox &&
                                (isDrawerFormOpen || isNonDrawerFormOpen) && (
                                    <GoogleRecaptchaCheckbox
                                        id={checkboxId}
                                        page={pageType}
                                        parent={`SignInForm${
                                            isInDrawer ? `_SignInDrawer` : ``
                                        }`}
                                    />
                                )}
                        </Grid.Item>
                        <Grid.Item className={`${componentClassname}__cta`}>
                            <RenderCTA {...{ validateForm }} />
                        </Grid.Item>
                        <Grid.Item
                            className={`${componentClassname}__forgot-password`}
                        >
                            <TextLink
                                href={linkAction?.fields?.link?.webUrl ?? '#'}
                                data-testid="forgot-password-link"
                                onClick={() => {
                                    if (!linkAction?.fields?.link?.webUrl) {
                                        openAccountDrawer(
                                            AccountDrawerType.FORGOT_PASSWORD
                                        );
                                    }
                                }}
                            >
                                {linkAction?.fields?.actionText ??
                                    'Forgot password?'}
                            </TextLink>
                        </Grid.Item>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
}
